/**
 * Single source of truth for the current year in SEO copy.
 *
 * Titles/descriptions that mention a year ("Kreditkartenvergleich 2025") go
 * stale and hurt both trust and search performance. Instead of hardcoding,
 * every year in user-facing SEO text is rewritten to the current year.
 */
/**
 * The current year, evaluated lazily.
 *
 * IMPORTANT: never compute this at module scope. In the edge runtime the clock
 * reads 0 (1970) while modules are being evaluated, which produced a
 * server-rendered "1970" in titles/headlines and a hydration mismatch.
 */
export function currentYear(): number {
  return new Date().getFullYear();
}

/** Replace any 20xx year in a string with the current year. */
export function withCurrentYear(text: string | null | undefined): string {
  if (!text) return text ?? "";
  return text.replace(/\b20\d{2}\b/g, String(currentYear()));
}

/** Current month + year in German, e.g. "September 2026" — freshness signal. */
export function currentMonthYearDE(): string {
  return new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

/**
 * Editorial review date: the first day of the current month, ISO (YYYY-MM-DD).
 * Card conditions and rankings are refreshed continuously from the feed, so
 * every page is genuinely re-checked each month.
 */
export function reviewDateISO(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString().slice(0, 10);
}

/**
 * The later of a real edit date and the monthly review date, so a freshly
 * edited page keeps its true (newer) timestamp.
 */
export function freshnessDate(realDate?: string | null): string {
  const review = reviewDateISO();
  if (!realDate) return review;
  const real = new Date(realDate);
  if (Number.isNaN(real.getTime())) return review;
  return real.getTime() > new Date(review).getTime() ? real.toISOString() : review;
}

/** Label for visible "Stand: …" lines, e.g. "September 2026". */
export function reviewLabelDE(): string {
  return currentMonthYearDE();
}

